import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  stainsService,
  stainProvidersService,
  CreateStainInput,
  UpdateStainInput,
  StainProvider,
  StainSearchParams,
  StartMarkerVerificationInput,
} from "../../services/admin/stains.service";

export function useStainsAdmin(params?: StainSearchParams) {
  return useQuery({
    queryKey: ["admin", "stains", params],
    queryFn: () => stainsService.getAll(params),
  });
}

export function useStain(id?: string) {
  return useQuery({
    queryKey: ["admin", "stains", id],
    queryFn: () => stainsService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateStainInput) => stainsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stains"] });
      queryClient.invalidateQueries({ queryKey: ["stains"] });
      notifications.show({
        title: "Success",
        message: "Stain created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create stain",
        color: "red",
      });
    },
  });
}

export function useUpdateStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateStainInput) => stainsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stains"] });
      queryClient.invalidateQueries({ queryKey: ["stains"] });
      notifications.show({
        title: "Success",
        message: "Stain updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update stain",
        color: "red",
      });
    },
  });
}

export function useDeleteStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => stainsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stains"] });
      queryClient.invalidateQueries({ queryKey: ["stains"] });
      notifications.show({
        title: "Success",
        message: "Stain deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete stain",
        color: "red",
      });
    },
  });
}

export function useToggleStainActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      stainsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stains"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

// =============================================
// Stain Providers Hooks
// =============================================

export function useStainProviders() {
  return useQuery({
    queryKey: ["admin", "stain-providers"],
    queryFn: stainProvidersService.getAll,
  });
}

export function useActiveStainProviders() {
  return useQuery({
    queryKey: ["admin", "stain-providers", "active"],
    queryFn: stainProvidersService.getActive,
  });
}

export function useCreateStainProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Partial<StainProvider>) =>
      stainProvidersService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stain-providers"] });
      notifications.show({
        title: "Success",
        message: "Provider created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create provider",
        color: "red",
      });
    },
  });
}

export function useUpdateStainProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: Partial<StainProvider> & { id: string }) =>
      stainProvidersService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stain-providers"] });
      notifications.show({
        title: "Success",
        message: "Provider updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update provider",
        color: "red",
      });
    },
  });
}

export function useDeleteStainProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => stainProvidersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stain-providers"] });
      notifications.show({
        title: "Success",
        message: "Provider deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete provider",
        color: "red",
      });
    },
  });
}

// =============================================
// Stain Provider Capabilities Hooks
// =============================================

export function useStainProviderCapabilities(stainId?: string) {
  return useQuery({
    queryKey: ["admin", "stain-provider-capabilities", stainId],
    queryFn: () => stainsService.getProviderCapabilities(stainId!),
    enabled: !!stainId,
  });
}

export function useAddStainProviderCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stainId,
      ...data
    }: {
      stainId: string;
      providerId: string;
      cost?: number;
      turnaroundDays?: number;
      isPreferred?: boolean;
    }) => stainsService.addProviderCapability(stainId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "stain-provider-capabilities", variables.stainId],
      });
      notifications.show({
        title: "Success",
        message: "Provider capability added successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add provider capability",
        color: "red",
      });
    },
  });
}

export function useUpdateStainProviderCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      capabilityId,
      stainId,
      ...data
    }: {
      capabilityId: string;
      stainId: string;
      cost?: number;
      turnaroundDays?: number;
      isPreferred?: boolean;
      isActive?: boolean;
    }) => stainsService.updateProviderCapability(capabilityId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "stain-provider-capabilities", variables.stainId],
      });
      notifications.show({
        title: "Success",
        message: "Provider capability updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update provider capability",
        color: "red",
      });
    },
  });
}

export function useDeleteStainProviderCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId }: { capabilityId: string; stainId: string }) =>
      stainsService.deleteProviderCapability(capabilityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "stain-provider-capabilities", variables.stainId],
      });
      notifications.show({
        title: "Success",
        message: "Provider capability removed successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to remove provider capability",
        color: "red",
      });
    },
  });
}

// =============================================
// Marker Verification Hooks
// =============================================

export function useStartStainMarkerVerification() {
  return useMutation({
    mutationFn: (input: StartMarkerVerificationInput) =>
      stainsService.startMarkerVerification(input),
    onSuccess: (data) => {
      notifications.show({
        title: data.dryRun ? "Dry Run Started" : "Verification Started",
        message: `Job ${data.jobId} created with ${data.total} tasks.`,
        color: "blue",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to start marker verification",
        color: "red",
      });
    },
  });
}

export function useStainMarkerVerificationJob(jobId?: string) {
  return useQuery({
    queryKey: ["admin", "stains", "marker-verification", "job", jobId],
    queryFn: () => stainsService.getMarkerVerificationJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;

      // Stop polling when job is paused
      if (data.status === "paused") return false;

      // Stop polling when no pending or sent tasks remain
      const hasInProgress =
        (data.summary.pending || 0) > 0 || (data.summary.sent || 0) > 0;

      return hasInProgress ? 2000 : false;
    },
  });
}

export function usePauseStainMarkerVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      stainsService.pauseMarkerVerificationJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "stains", "marker-verification", "job", jobId],
      });
      notifications.show({
        title: "Job Paused",
        message: "Verification job has been paused.",
        color: "orange",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to pause verification job",
        color: "red",
      });
    },
  });
}

export function useResumeStainMarkerVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      stainsService.resumeMarkerVerificationJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "stains", "marker-verification", "job", jobId],
      });
      notifications.show({
        title: "Job Resumed",
        message: "Verification job has been resumed.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to resume verification job",
        color: "red",
      });
    },
  });
}
